import React, { useEffect, useState } from 'react';

/**
 * Pre-roll splash that plays before the first Pulse card.
 * Shows once per browser session (sessionStorage). 2.0s total.
 *
 * Uses the canonical AUDIVUE eye (`/favicon-192x192.png`) + wordmark
 * (`/wordmark-tight.png`) — same assets as HeaderLockup — so the brand
 * mark on the first screen matches the rest of the site.
 *
 * Stages:
 *  0-1.0s  -> Animated AUDIVUE eye + wordmark
 *  1.0-2.0s -> Cross-fade to "@artist · song title" of the first card
 */
export const PulsePreroll = ({ firstVideo, onDone }) => {
  const [stage, setStage] = useState(0); // 0 = logo, 1 = artist info, 2 = fading out

  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 1000);
    const t2 = setTimeout(() => setStage(2), 1700);
    const t3 = setTimeout(() => onDone && onDone(), 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onDone]);

  const artistHandle = (firstVideo?.artist || firstVideo?.username || 'audivue')
    .toString().replace(/\s+/g, '_').toLowerCase();
  const title = firstVideo?.title || 'Welcome';

  return (
    <div
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black transition-opacity duration-300 ${
        stage === 2 ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      data-testid="pulse-preroll"
    >
      {/* Stage 0: Full AUDIVUE + eye + "MUSIC VIDEOS, REBORN." lockup, served
          as a single black-backed JPEG so its native neon-on-black blends
          edge-to-edge with the page background (no boxes, no glow rings,
          no borders that would betray the asset boundary). */}
      <div
        className={`relative flex flex-col items-center transition-all duration-500 ${
          stage === 0 ? 'opacity-100 scale-100' : 'opacity-0 scale-95 -translate-y-4'
        }`}
      >
        <img
          src="/pulse-lockup.jpg"
          alt="AUDIVUE — Music Videos, Reborn"
          className="block w-[88vw] max-w-[640px] h-auto select-none pointer-events-none"
          draggable={false}
          style={{
            animation: 'preroll-logo-in 700ms cubic-bezier(0.2, 0.8, 0.2, 1)',
            // mix-blend-mode: screen → pure-black pixels turn invisible against
            // the page's bg-black, so the lockup floats edge-to-edge with no
            // visible image boundary.
            mixBlendMode: 'screen',
            // Clip the outer 8px on each side — JPEG compression introduces
            // faintly-above-black pixels at the borders that survive `screen`
            // blending and look like thin vertical bars otherwise.
            clipPath: 'inset(4px 8px)',
          }}
        />
      </div>

      {/* Stage 1: Artist tag */}
      <div
        className={`absolute flex flex-col items-center transition-all duration-500 ${
          stage === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
        data-testid="pulse-preroll-artist"
      >
        <span className="text-xs uppercase tracking-[0.3em] text-orange-400/80 mb-3">Now Playing</span>
        <span className="text-2xl font-semibold text-white">@{artistHandle}</span>
        <span className="mt-2 text-base text-zinc-300 max-w-[80vw] text-center line-clamp-1">
          {title}
        </span>
      </div>
    </div>
  );
};

PulsePreroll.shouldShow = () => {
  try {
    return sessionStorage.getItem('pulse_preroll_seen') !== '1';
  } catch {
    return true;
  }
};

PulsePreroll.markSeen = () => {
  try { sessionStorage.setItem('pulse_preroll_seen', '1'); } catch (_e) { /* noop */ }
};
